import React, { type ChangeEvent, type FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { type AsyncState } from 'react-use/lib/useAsync';
import styled from 'styled-components';

import {
  Action,
  Dropdown,
  InlineLoader,
  PredictiveTextField,
  Text,
  TextField,
  colors,
} from '@soluto-private/mx-asurion-ui-react';

import { type Suggestion } from '@service-initiation/shared-data-access-google';
import { STATE_DROPDOWN_OPTIONS } from '@service-initiation/shared-data-access-us-states';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import { type GooglePlacesApiExtractedAddress } from '@service-initiation/tech-care-data-access-store';
import { ErrorType } from '@service-initiation/tech-care-data-access-tech-care-services';

const MailInHelperText = styled.div`
  margin: 6px;
  color: ${colors.errorDeep};
`;

const MAIL_IN_TEXT = TranslationEntries.Repairs.StoreLocator.MailIn;
const REMOTE_TECH_TEXT = TranslationEntries.Repairs.StoreLocator.RemoteTech;

const BottomInputsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px;
`;

const AddressWrapper = styled.div`
  .manual-address-input {
    margin-bottom: 1em;
  }
`;

const AddressTextField = styled(TextField)`
  input {
    margin-top: 1em;
    margin-bottom: 1em;
  }
`;

export interface DropdownItem {
  name: string;
  value: string;
}

export type PredictiveManualAddressInputProps = {
  handleManualFieldChange: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  handleTextChange: (s: string) => void;
  inputText?: string;
  manualInputFields?: GooglePlacesApiExtractedAddress;
  onLocationSelect: (place: Suggestion) => void;
  predictions?: Suggestion[];
  state: AsyncState<boolean>;
  validationError?: ErrorType;
};

export const PredictiveManualAddressInput: FC<
  PredictiveManualAddressInputProps
> = ({
  handleManualFieldChange,
  handleTextChange,
  inputText,
  manualInputFields,
  onLocationSelect,
  predictions,
  state,
  validationError,
}) => {
  const [showManualForm, setShowManualForm] = useState(false);
  const { t } = useTranslation();

  const errorToMessageMap = {
    [ErrorType.RemoteTechDeviceIneligible]:
      REMOTE_TECH_TEXT.RemoteTechIneligibleDeviceHelperText,
    [ErrorType.RemoteTechLocationIneligible]:
      REMOTE_TECH_TEXT.RemoteTechIneligibleLocationHelperText,
  };

  return (
    <>
      <PredictiveTextField
        alertNoResults={false}
        autoComplete={showManualForm ? 'off' : 'street-address'}
        className="fs-mask"
        data-testid="predictive-address-input"
        disabled={state.loading || showManualForm}
        fieldStatus={validationError ? 'error' : 'default'}
        handleChange={(s: string) => handleTextChange(s)}
        iconSrc={
          state.loading ? (
            <InlineLoader disabled={true} width="1.5rem" />
          ) : (
            'location-map'
          )
        }
        label={t(MAIL_IN_TEXT.TextFieldLabel)}
        onSelection={onLocationSelect}
        shouldFocusFirstItem={false}
        style={{ color: 'black' }}
        suggestions={predictions && [...predictions]}
        value={inputText}
      />
      {validationError && (
        <MailInHelperText data-testid="helper-text">
          {(validationError === ErrorType.RemoteTechLocationIneligible ||
            validationError === ErrorType.RemoteTechDeviceIneligible) &&
          !showManualForm
            ? t(errorToMessageMap[validationError])
            : validationError !== ErrorType.RemoteTechLocationIneligible &&
              validationError !== ErrorType.RemoteTechDeviceIneligible && (
                <Trans
                  components={{
                    Text: <Text size={1} weight="feather" />,
                  }}
                  i18nKey={MAIL_IN_TEXT.TextFieldHelper}
                />
              )}
        </MailInHelperText>
      )}
      <Action
        color="secondary"
        data-testid="cant-find-address-button"
        onClick={() => setShowManualForm((prevValue) => !prevValue)}
        variant="flat"
      >
        {t(MAIL_IN_TEXT.ManualFormLabelText)}
      </Action>
      {showManualForm && (
        <AddressWrapper data-testid="manual-address-form">
          <AddressTextField
            autoComplete="address-line1"
            containerClassName="manual-address-input"
            data-testid="manual-address-street-address-input"
            fieldStatus={validationError ? 'error' : 'default'}
            helperText={
              validationError === ErrorType.RemoteTechDeviceIneligible ||
              validationError === ErrorType.RemoteTechLocationIneligible
                ? t(errorToMessageMap[validationError])
                : undefined
            }
            inputClassName="fs-mask"
            label="Street Address"
            name="streetAddress"
            onChange={handleManualFieldChange}
            required={true}
            value={manualInputFields?.streetAddress}
          />
          <AddressTextField
            autoComplete="address-line2"
            containerClassName="manual-address-input"
            data-testid="manual-address-2-input"
            inputClassName="fs-mask"
            label="Apartment, unit, suite (optional)"
            name="address2"
            onChange={handleManualFieldChange}
            required={false}
            value={manualInputFields?.address2}
          />
          <AddressTextField
            autoComplete="address-level2"
            containerClassName="manual-address-input"
            data-testid="manual-address-city-input"
            inputClassName="fs-mask"
            label="City"
            name="city"
            onChange={handleManualFieldChange}
            required={true}
            value={manualInputFields?.city}
          />
          <BottomInputsWrapper>
            <Dropdown
              autoComplete="address-level1"
              className="fs-mask"
              containerClassName="manual-address-state-dropdown"
              data-testid="manual-address-state-dropdown"
              id="manual-address-state"
              label="State"
              name="state"
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange={(event: ChangeEvent<any>) => {
                handleManualFieldChange({
                  target: {
                    name: 'state',
                    value: event.target.value,
                  },
                } as ChangeEvent<HTMLInputElement>);
              }}
              options={STATE_DROPDOWN_OPTIONS}
              required
              value={manualInputFields?.state}
            />
            <AddressTextField
              autoComplete="postal-code"
              containerClassName="manual-address-input"
              data-testid="manual-address-zip-input"
              id="manual-address-zip"
              inputClassName="fs-mask"
              inputMode="numeric"
              label="Zip"
              maxLength={5}
              name="zip"
              onChange={handleManualFieldChange}
              required={true}
              value={manualInputFields?.zip}
            />
          </BottomInputsWrapper>
        </AddressWrapper>
      )}
    </>
  );
};
